import React from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';

const StyledButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${theme.typography.fontFamily};
  font-weight: ${props => props.fontWeight || theme.typography.fontWeights.medium};
  font-size: ${props => props.fontSize || theme.typography.fontSizes.base};
  padding: ${props => props.padding || `${theme.spacing.sm} ${theme.spacing.lg}`};
  border: ${props => props.border || `1px solid ${theme.colors.border}`};
  border-radius: ${props => props.borderRadius || theme.borderRadius.md};
  background-color: ${props => props.backgroundColor || theme.colors.primaryDark};
  color: ${props => props.color || theme.colors.text};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.4 : 1};
  transition: ${theme.transitions.normal};
  outline: none;
  user-select: none;
  white-space: nowrap;
  letter-spacing: ${theme.typography.letterSpacing.tight};
  box-shadow: ${props => props.boxShadow || theme.shadows.sm};
  min-width: ${props => props.minWidth || 'auto'};
  width: ${props => props.width || 'auto'};
  height: ${props => props.height || 'auto'};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.hoverBackgroundColor || theme.colors.primary};
    transform: ${props => props.hoverTransform || 'none'};
    box-shadow: ${props => props.hoverBoxShadow || theme.shadows.md};
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:focus {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const PrimaryButton = styled(StyledButton)`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryDark} 100%);
  border: none;
  color: white;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, ${theme.colors.primaryHover} 0%, ${theme.colors.primary} 100%);
  }
`;

const GhostButton = styled(StyledButton)`
  background: transparent;
  color: ${theme.colors.textSecondary};
  border: 1px solid ${theme.colors.border};
  box-shadow: none;
  
  &:hover:not(:disabled) {
    background-color: ${theme.colors.surfaceHover};
    color: ${theme.colors.text};
    border-color: ${theme.colors.primary};
  }
`;

const IconButton = styled(StyledButton)`
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  min-width: ${props => props.size || '40px'};
  height: ${props => props.size || '40px'};
  width: ${props => props.size || '40px'};
  
  svg {
    width: ${props => props.iconSize || '16px'};
    height: ${props => props.iconSize || '16px'};
  }
`;

const PlayButton = styled(StyledButton)`
  position: absolute;
  left: 0px;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.full};
  border: 2px solid ${theme.colors.text};
  z-index: 10;
  padding: 0;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const NavigationButton = styled(StyledButton)`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: none;
  border: none;
  padding: 0;
  font-size: ${theme.typography.fontSizes.xxxl};
  
  svg {
    width: 28px;
    height: 28px;
  }
  
  &:hover:not(:disabled) {
    background-color: ${theme.colors.surfaceHover};
    transform: none;
  }
`;

const Button = ({ 
  children, 
  variant = 'default',
  size,
  iconSize,
  disabled = false,
  onClick,
  type = 'button',
  ...props 
}) => {
  const buttonProps = {
    disabled,
    onClick,
    type,
    ...props
  };

  switch (variant) {
    case 'primary':
      return (
        <PrimaryButton {...buttonProps}>
          {children}
        </PrimaryButton>
      );
    case 'ghost':
      return (
        <GhostButton {...buttonProps}>
          {children}
        </GhostButton>
      );
    case 'icon':
      return (
        <IconButton size={size} iconSize={iconSize} {...buttonProps}>
          {children}
        </IconButton>
      );
    case 'play':
      return (
        <PlayButton {...buttonProps}>
          {children}
        </PlayButton>
      );
    case 'navigation':
      return (
        <NavigationButton {...buttonProps}>
          {children}
        </NavigationButton>
      );
    default:
      return (
        <StyledButton {...buttonProps}>
          {children}
        </StyledButton>
      );
  }
};

export default Button; 